import { ComponentRef, Directive, EventEmitter, inject, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { INode } from '@app/core/libraries/Tree/core/interfaces/INode';
import { ITreeConfig } from '@app/core/libraries/Tree/core/interfaces/ITreeConfig';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TreeAddOptionsComponent } from './components/add-options/tree-add-options.component';
import { TreeCommonComponent } from './components/common/tree-common.component';

const componentMapper = {
    common: TreeCommonComponent,
    addOptions: TreeAddOptionsComponent,
};

@Directive({
    selector: '[treeComponent]',
    exportAs: 'treeComponent',
    providers: [FuiDestroyService],
})
/** Директива для работы с разными видами деревьев */
export class TreeDirective implements OnInit {
    readonly #destroy$ = inject(FuiDestroyService);
    private container = inject(ViewContainerRef);

    /** Конфиг дерева */
    @Input() config: ITreeConfig;

    /** Объект для создания компонента */
    @Input() componentRef: ComponentRef<TreeCommonComponent | TreeAddOptionsComponent>;

    @Output() eventAdd: EventEmitter<INode> = new EventEmitter<INode>();
    @Output() selectItem: EventEmitter<INode | INode[]> = new EventEmitter<INode | INode[]>();

    @Output() validateTree$: BehaviorSubject<{ isError: boolean }> = new BehaviorSubject<{ isError: boolean }>({
        isError: false,
    });

    /** Инициализация */
    ngOnInit() {
        this.componentRef = this.container.createComponent(componentMapper[this.config.type]);
        this.componentRef.instance.service.config = this.config;

        // Передаем событие добавления
        if (this.componentRef.instance.hasOwnProperty('eventAdd')) {
            this.componentRef.instance?.eventAdd.pipe(takeUntil(this.#destroy$)).subscribe((next) => {
                this.eventAdd.next(next);
            });
        }
        // Передаем событие добавления
        if (this.componentRef.instance.hasOwnProperty('validateTree')) {
            this.componentRef.instance?.validateTree.pipe(takeUntil(this.#destroy$)).subscribe((next) => {
                this.validateTree$.next(next);
            });
        }
        // Передаем событие выбора элементов
        if (this.componentRef.instance.hasOwnProperty('selectItem')) {
            this.componentRef.instance?.selectItem.pipe(takeUntil(this.#destroy$)).subscribe((next) => {
                this.selectItem.emit(next);
            });
        }
    }

    /** Получить состояние выбранных элементов дерева */
    public getSelectedList(): INode[] {
        const newData: INode[] = [];
        const component = this.componentRef.instance;
        const treeData = component.service.treeData;
        const selectedModel = component.service.checklistSelection;
        // Обновляем dataNodes чтобы там содержались все нужные узлы
        component.treeControl.dataNodes = Array.from(component.flatNodeMap.values());
        treeData.forEach((item) => {
            const node = component.flatNodeMap.get(item.id);
            const descendants = component.treeControl.getDescendants(node);
            const allDescendantsSelected = descendants.every((d) => selectedModel.isSelected(String(d.id)));
            const nodeSelected = selectedModel.isSelected(String(node?.id));
            // Если узел выбран и все его потомки выбраны, добавляем его
            if (nodeSelected && allDescendantsSelected) {
                newData.push(this.getCloneNode(item));
            }
            descendants.forEach((child) => {
                if (selectedModel.isSelected(String(child?.id))) {
                    newData.push(this.getCloneNode(child));
                } else if (component.hasChild(0, child) && component.descendantsPartiallySelected(child)) {
                    newData.push(this.getCloneNode(child));
                }
            });
        });

        if (newData.length && this.config.additionalOptions && component instanceof TreeAddOptionsComponent) {
            newData.forEach((item) => {
                const node = component.flatNodeMap.get(item.id);
                const descendants = component.treeControl.getDescendants(node);
                if (descendants.length) {
                    this.config.additionalOptions.forEach((option: { field: string }) => {
                        if (item[option.field] !== '1') {
                            item[option.field] = component?.indeterminateOption(node, option);
                        }
                        descendants.forEach((des) => {
                            if (des[option.field] !== '1') {
                                des[option.field] = component?.indeterminateOption(des, option);
                            }
                        });
                    });
                }
            });
        }
        return newData;
    }

    /** Получить выбранный элемент дерева */
    public getSelectedItem(): INode {
        return this.componentRef.instance.service.selectNode;
    }

    public getCloneNode(node: object) {
        const newNode = {};
        Object.keys(node)
            .filter((key) => key !== 'children')
            .forEach((key) => {
                newNode[key] = node[key];
            });
        return newNode;
    }
}
